import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, type AnimeArticle } from '@/mocks/anime';

const articleImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=Death%20Note%20anime%20art%20style%2C%20dark%20atmosphere%2C%20a%20mysterious%20black%20notebook%20on%20a%20wooden%20desk%20with%20a%20single%20candle%20casting%20dramatic%20shadows%2C%20gothic%20aesthetic%2C%20deep%20crimson%20and%20black%20tones%2C%20minimalist%20composition%2C%20high%20contrast&width=400&height=560&seq=death-note-card&orientation=portrait',
  '2': 'https://readdy.ai/api/search-image?query=Attack%20on%20Titan%20anime%20epic%20style%2C%20a%20lone%20soldier%20with%20cape%20facing%20a%20massive%20colossal%20figure%20in%20the%20distance%2C%20golden%20sunset%20sky%2C%20dramatic%20scale%2C%20warm%20amber%20lighting%2C%20cinematic%20atmosphere%2C%20powerful%20silhouette&width=400&height=560&seq=aot-card-2026&orientation=portrait',
  '3': 'https://readdy.ai/api/search-image?query=Psycho%20Pass%20anime%20futuristic%20style%2C%20a%20sleek%20holographic%20interface%20displaying%20a%20glowing%20crime%20coefficient%20number%20in%20a%20dark%20surveillance%20room%2C%20neon%20amber%20accents%2C%20cold%20metallic%20surfaces%2C%20cyberpunk%20atmosphere%2C%20clean%20minimalist%20composition&width=400&height=560&seq=psycho-pass-card&orientation=portrait',
  '4': 'https://readdy.ai/api/search-image?query=Monster%20anime%20psychological%20thriller%20style%2C%20a%20hospital%20corridor%20at%20night%20with%20dim%20flickering%20lights%2C%20a%20single%20shadowy%20figure%20standing%20at%20the%20far%20end%2C%20cold%20atmosphere%2C%20deep%20shadows%2C%20ominous%20mood%2C%20high%20tension&width=400&height=560&seq=monster-card&orientation=portrait',
  '5': 'https://readdy.ai/api/search-image?query=Steins%20Gate%20anime%20sci-fi%20style%2C%20a%20vintage%20computer%20monitor%20glowing%20in%20a%20dim%20cluttered%20laboratory%2C%20wires%20and%20gadgets%20scattered%20around%2C%20warm%20amber%20screen%20glow%2C%20mysterious%20atmosphere%2C%20time%20travel%20theme&width=400&height=560&seq=steins-card&orientation=portrait',
  '6': 'https://readdy.ai/api/search-image?query=Nana%20anime%20romance%20style%2C%20a%20vintage%20microphone%20on%20a%20dimly%20lit%20stage%2C%20warm%20spotlight%2C%20rose%20petals%20scattered%2C%20emotional%20atmosphere%2C%20punk%20rock%20aesthetic%2C%20soft%20focus&width=400&height=560&seq=nana-card&orientation=portrait',
  '7': 'https://readdy.ai/api/search-image?query=Code%20Geass%20anime%20mecha%20style%2C%20a%20masked%20figure%20in%20a%20flowing%20cape%20standing%20on%20a%20rooftop%2C%20dramatic%20sunset%20sky%2C%20city%20in%20background%2C%20regal%20purple%20and%20gold%20accents&width=400&height=560&seq=geass-card&orientation=portrait',
  '8': 'https://readdy.ai/api/search-image?query=Paranoia%20Agent%20anime%20surreal%20style%2C%20a%20shadowy%20figure%20with%20a%20bent%20baseball%20bat%20on%20a%20deserted%20street%20at%20night%2C%20single%20streetlamp%20illuminating%2C%20uncanny%20atmosphere%2C%20surreal%20vibe&width=400&height=560&seq=paranoia-card&orientation=portrait',
  '9': 'https://readdy.ai/api/search-image?query=Vinland%20Saga%20anime%20viking%20epic%20style%2C%20a%20lone%20warrior%20standing%20on%20a%20cliff%20overlooking%20a%20stormy%20sea%2C%20dramatic%20clouds%2C%20golden%20light%20breaking%20through%2C%20norse%20aesthetic&width=400&height=560&seq=vinland-card&orientation=portrait',
  '10': 'https://readdy.ai/api/search-image?query=Erased%20anime%20mystery%20style%2C%20a%20snow-covered%20winter%20street%20in%20a%20quiet%20Japanese%20town%2C%20a%20single%20figure%20walking%20away%2C%20nostalgic%20atmosphere%2C%20soft%20blue%20hour%20lighting&width=400&height=560&seq=erased-card&orientation=portrait',
  '11': 'https://readdy.ai/api/search-image?query=Mushishi%20anime%20ethereal%20style%2C%20a%20solitary%20traveler%20with%20white%20hair%20walking%20through%20a%20misty%20bamboo%20forest%2C%20soft%20diffused%20light%20filtering%20through%20leaves%2C%20luminous%20floating%20particles%2C%20serene%20atmosphere%2C%20watercolor%20texture%2C%20gentle%20green%20and%20gold%20tones&width=400&height=560&seq=mushishi-card&orientation=portrait',
  '12': 'https://readdy.ai/api/search-image?query=Showa%20Genroku%20Rakugo%20Shinju%20anime%20traditional%20style%2C%20a%20kimono-clad%20storyteller%20kneeling%20on%20a%20tatami%20stage%20with%20a%20folding%20fan%20under%20warm%20lantern%20light%2C%20traditional%20Japanese%20theater%20setting%2C%20golden%20amber%20lighting%2C%20elegant%20composition&width=400&height=560&seq=rakugo-card&orientation=portrait',
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article: AnimeArticle | undefined = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-lg">{t('noResults')}</p>
            <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-6" aria-label="breadcrumb">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {t('breadcrumb.home')}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  {article.category}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">{article.title}</span>
              </nav>

              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full lg:w-[300px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={articleImages[article.id] || ''}
                      alt={article.title}
                      className="w-full aspect-[3/4] object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-md font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-500">{article.year}年</span>
                  </div>

                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                    {article.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-6">{article.titleJa}</p>

                  <div className="flex flex-wrap items-center gap-6 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-star-fill text-2xl text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground-900">{article.rating}</p>
                        <p className="text-xs text-foreground-500">{t('article.rating')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-film-line text-2xl text-foreground-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{article.episodes}</p>
                        <p className="text-xs text-foreground-500">{t('article.episodes')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-calendar-line text-2xl text-foreground-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{article.year}</p>
                        <p className="text-xs text-foreground-500">{t('article.year')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.tags.map((tag) => (
                      <span key={tag} className="bg-secondary-100 text-secondary-900 text-xs px-2.5 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {t('article.synopsis')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {article.synopsis}
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  作品亮点
                </h2>
                <div className="bg-background-100 rounded-lg p-5 md:p-6">
                  <ul className="flex flex-col gap-3">
                    {article.highlights.map((hl, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground-700">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-check-double-line text-accent-500"></i>
                        </div>
                        {hl}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {t('article.characters')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {article.characters.map((ch, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-4">
                      <h4 className="font-heading font-bold text-foreground-900 text-sm mb-1">{ch.name}</h4>
                      <span className="inline-block text-xs text-accent-600 bg-accent-50 px-2 py-0.5 rounded mb-2">{ch.role}</span>
                      <p className="text-xs text-foreground-600 leading-relaxed">{ch.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {t('article.watchGuide')}
                </h2>
                <div className="bg-secondary-50 border border-secondary-200/70 rounded-lg p-5 md:p-6">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {article.watchGuide}
                  </p>
                </div>
              </section>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-3 bg-background-50 rounded-lg p-3 border border-background-200/70 hover:border-primary-300 transition-all"
                  >
                    <div className="w-16 h-20 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={articleImages[ra.id] || ''}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex flex-col justify-between min-w-0">
                      <h4 className="text-xs font-heading font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500 flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        {ra.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}